import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import {
  PauseCircleFill,
  Search,
  SendFill,
  FileEarmarkXFill,
  CheckCircleFill,
  XCircleFill,
  ExclamationCircle,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { formatDateTimeExtratoList, normalizeText } from "../../../../utils";
import type { ExtractRow } from "../shared/extractListTypes";
import { PATHS } from "../../../../routes/paths";
import "../ExtractTable/ExtractTable.css";
import "./ExtractSindicatoTable.css";

export interface ExtractSindicatoTableProps {
  currentItems: ExtractRow[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  selectedYear?: number;
  formatDate?: (value: string | null | undefined) => string;
}

const getSituationConfig = (situacaoRaw: string | undefined): { className: string; icon: React.ReactNode } => {
  const s = normalizeText(situacaoRaw);

  if (s.includes("analise nao finalizada")) {
    return { className: "status-plain", icon: <PauseCircleFill color="#D7961E" /> };
  }

  if (s.includes("extrato pendente")) {
    return { className: "status-pendente-analista", icon: <Search /> };
  }

  if (s.includes("extrato enviado")) {
    return { className: "status-enviado", icon: <SendFill /> };
  }

  if (s.includes("extrato validado")) {
    return { className: "status-plain", icon: <CheckCircleFill color="#067647" /> };
  }

  if (s.includes("inapto")) {
    return { className: "status-plain", icon: <XCircleFill color="#BA0C2F" /> };
  }

  if (s.includes("recusado") || s.includes("nao validado") || s.includes("naovalidado")) {
    return { className: "status-adequacao-recusa", icon: <FileEarmarkXFill /> };
  }

  return { className: "status-default", icon: <ExclamationCircle /> };
};

const ExtractSindicatoTable = ({
  currentItems,
  handleSort,
  getSortIcon,
  selectedYear,
  formatDate = formatDateTimeExtratoList,
}: ExtractSindicatoTableProps) => {
  const navigate = useNavigate();

  const handleView = (item: ExtractRow) => {
    const solicitacaoId = item.solicitacaoId ?? item.questionario;
    if (!solicitacaoId) return;

    navigate(
      PATHS.EXTRACT_SINDICATO_DETALHE.replace(
        ":solicitacaoAvaliacaoId",
        String(solicitacaoId),
      ),
      {
        state: {
          selectedYear: selectedYear ?? item.ano,
          solicitacao: item.solicitacao,
          idSindicato: item.idSindicato,
          situacao: item.situacao,
        },
      },
    );
  };

  return (
    <Table bordered hover responsive className="extract-table extract-sindicato-table">
      <thead>
        <tr>
          <th
            onClick={() => handleSort("solicitacao")}
            className="sortable-header"
          >
            Solicitação {getSortIcon("solicitacao")}
          </th>
          <th
            onClick={() => handleSort("situacao")}
            className="sortable-header extract-col-situacao"
          >
            Situação {getSortIcon("situacao")}
          </th>
          <th>Envio Extrato</th>
          <th
            onClick={() => handleSort("atualizacao")}
            className="sortable-header"
          >
            Última Atualização {getSortIcon("atualizacao")}
          </th>
          <th className="text-center">Ações</th>
        </tr>
      </thead>
      <tbody>
        {currentItems.length === 0 && (
          <tr>
            <td colSpan={5} className="empty-row">
              Nenhum extrato encontrado.
            </td>
          </tr>
        )}
        {currentItems.map((item) => {
          const solicitacaoId = item.solicitacaoId ?? item.questionario;
          const situacaoNorm = normalizeText(item.situacao);
          const podeVisualizar =
            !!solicitacaoId &&
            Number(solicitacaoId) > 0 &&
            (situacaoNorm.includes("extrato enviado") ||
              situacaoNorm.includes("extrato recusado") ||
              situacaoNorm.includes("extrato validado") ||
              (situacaoNorm.includes("inapto") && !!item.envio));
          const { className, icon } = getSituationConfig(item.situacao);

          return (
            <tr key={item.rowKey ?? String(solicitacaoId ?? item.solicitacao)}>
              <td>{item.solicitacao || "—"}</td>
              <td className="extract-col-situacao">
                <div className="situation-cell">
                  <span className={`situation-icon ${className}`}>{icon}</span>
                  <span className="situation-text">{item.situacao || "—"}</span>
                </div>
              </td>
              <td>{formatDate(item.envio)}</td>
              <td>{formatDate(item.atualizacao)}</td>
              <td>
                <div className="action-buttons d-flex gap-2 justify-content-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {podeVisualizar
                          ? "Visualizar extrato"
                          : "Disponível apenas quando o extrato for enviado"}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Button
                        className="btn-view"
                        size="sm"
                        onClick={() => handleView(item)}
                        disabled={!podeVisualizar}
                        style={
                          !podeVisualizar
                            ? { opacity: 0.2, cursor: "not-allowed" }
                            : undefined
                        }
                      >
                        <FaEye />
                      </Button>
                    </span>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default ExtractSindicatoTable;
